import PropTypes from 'prop-types';
import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import IconButton from 'components/@extended/IconButton';
import { AuditOutlined, FileExclamationOutlined, CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined } from '@ant-design/icons';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export default function NavItem({ item, level, isParents = false, setSelectedID }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID(item.id);
    }
  };

  const override = {
    'oportunidades': AuditOutlined,
    'pendiente-boletin': FileExclamationOutlined,
    'terminados': CheckCircleOutlined,
    'rechazadas': CloseCircleOutlined,
    '2025': CalendarOutlined
  };
  const Icon = override[item.id] || item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem',
        ...(isParents && { fontSize: 20, stroke: '1.5' })
      }}
    />
  ) : (
    false
  );

  const { pathname } = useLocation();
  const normalizedPathname = pathname;

  const targetPath = item?.link ? item.link : item.url;
  const normalizedTarget = targetPath;
  // Selección base (permite exacto opcional)
  let isSelected = !!matchPath({ path: normalizedTarget, end: Boolean(item?.exact) }, normalizedPathname);
  if (!isSelected && Array.isArray(item?.activeUrls) && item.activeUrls.length) {
    isSelected = item.activeUrls.some((activeUrl) =>
      !!matchPath({ path: activeUrl, end: true }, normalizedPathname)
    );
  }
  // Fallback para Iberdrola: normaliza '/i' para que resalte en ambas regiones
  if (!isSelected) {
    const url = targetPath;
    const norm = (s) => (s || '').replace(/^\/(i(?:_v)?)/, '/i');
    const nUrl = norm(url);
    const nPath = norm(pathname);
    if (nUrl && nPath) {
      if (item?.exact) {
        isSelected = nPath === nUrl;
      } else if (nPath.startsWith(nUrl)) {
        isSelected = true;
      }
    }
  }

  if (item.onClick) {
    isSelected = false;
  }
  if (!isSelected && pathname.startsWith('/cuadros') && item.id === 'gestion-obras') {
    isSelected = true;
  }
  if (!isSelected && pathname.includes('/gestion-proyectos') && item.id === 'gestion-obras') {
    isSelected = true;
  }
  if (pathname.includes('/gestion-proyectos/unidades-obra') && item.id === 'gestion-obras') {
    isSelected = false;
  }
  if (!isSelected && pathname.includes('/gestion-proyectos/unidades-obra') && item.id === 'gestion-proyectos-unidades-obra') {
    isSelected = true;
  }
  if (
    pathname.includes('/gestion-proyectos/unidades-obra') &&
    item.id !== 'gestion-proyectos-unidades-obra' &&
    String(item?.url || '').includes('/gestion-proyectos')
  ) {
    isSelected = false;
  }
  if (item.skipSelection) {
    isSelected = false;
  }
  // Iberdrola-specific green highlight for selected items (limited set)
  const isIberdrola = (() => {
    try {
      if (pathname.startsWith('/i')) return true;
      if (typeof window !== 'undefined' && window.location?.pathname?.startsWith('/i')) return true;
    } catch {}
    try {
      return localStorage.getItem('current_area') === 'i';
    } catch {}
    return false;
  })();
  const iberIds = new Set([
    'dashboard',
    'clientes',
    'oportunidades',
    'visitas',
    'presupuestos-oportunidades',
    'instalaciones',
    'pendiente-boletin',
    'terminados',
    'rechazadas',
    'navigation-contactos',
    'navigation-agenda',
    'login1',
    'areas'
  ]);
  const useIberGreen = isIberdrola && iberIds.has(item.id);
  const greenText = 'rgba(66,133,61,1)';
  const greenBg = 'rgba(232,245,233,1)';

  const textColor = 'text.primary';
  const iconSelectedColor = useIberGreen ? greenText : 'primary.main';

  const handleClick = (event) => {
    let preventNavigation = false;
    if (item.onClick) {
      const result = item.onClick(event);
      if (result === false) {
        preventNavigation = true;
      }
    }
    if (preventNavigation) {
      event.preventDefault();
    }
    itemHandler();
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <ListItemButton
          component={Link}
          to={item.url}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          sx={(theme) => ({
            zIndex: 1201,
            pl: drawerOpen ? `${level * 28}px` : 1.5,
            py: !drawerOpen && level === 1 ? 1.25 : 1,
            ...(drawerOpen && {
              '&:hover': { bgcolor: useIberGreen ? greenBg : 'primary.lighter', ...theme.applyStyles('dark', { bgcolor: 'divider' }) },
              '&.Mui-selected': {
                bgcolor: useIberGreen ? greenBg : 'primary.lighter',
                ...theme.applyStyles('dark', { bgcolor: 'divider' }),
                borderRight: '2px solid',
                borderColor: useIberGreen ? greenText : 'primary.main',
                color: iconSelectedColor,
                '&:hover': { color: iconSelectedColor, bgcolor: useIberGreen ? greenBg : 'primary.lighter', ...theme.applyStyles('dark', { bgcolor: 'divider' }) }
              },
              '&:hover .MuiListItemIcon-root': { color: iconSelectedColor },
              '&.Mui-selected .MuiListItemIcon-root': { color: iconSelectedColor },
              '&:hover .MuiTypography-root': { color: iconSelectedColor },
              '&.Mui-selected .MuiTypography-root': { color: iconSelectedColor }
            }),
            ...(!drawerOpen && {
              '&:hover': { bgcolor: 'transparent' },
              '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' },
              // force icon color on hover/selected when collapsed
              '&:hover .MuiListItemIcon-root': { color: iconSelectedColor },
              '&.Mui-selected .MuiListItemIcon-root': { color: iconSelectedColor }
            })
          })}
          onClick={handleClick}
        >
          {itemIcon && (
            <ListItemIcon
              sx={(theme) => ({
                minWidth: 28,
                color: isSelected ? iconSelectedColor : textColor,
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 36,
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { bgcolor: useIberGreen ? greenBg : 'secondary.lighter', ...theme.applyStyles('dark', { bgcolor: useIberGreen ? greenBg : 'secondary.light' }) }
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: useIberGreen ? greenBg : 'primary.lighter',
                    ...theme.applyStyles('dark', { bgcolor: useIberGreen ? greenBg : 'primary.900' }),
                    '&:hover': { bgcolor: useIberGreen ? greenBg : 'primary.lighter', ...theme.applyStyles('dark', { bgcolor: useIberGreen ? greenBg : 'primary.darker' }) }
                  })
              })}
            >
              {itemIcon}
            </ListItemIcon>
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && (
            <ListItemText
              primary={
                <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor }}>
                  {item.title}
                </Typography>
              }
            />
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
        {(drawerOpen || (!drawerOpen && level !== 1)) &&
          item?.actions &&
          item?.actions.map((action, index) => {
            const ActionIcon = action.icon;
            const callAction = action?.function;
            return (
              <IconButton
                key={index}
                {...(action.type === 'function' && {
                  onClick: (event) => {
                    event.stopPropagation();
                    callAction();
                  }
                })}
                {...(action.type === 'link' && {
                  component: Link,
                  to: action.url,
                  target: action.target ? '_blank' : '_self'
                })}
                color="secondary"
                variant="outlined"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 20,
                  zIndex: 1202,
                  width: 20,
                  height: 20,
                  mr: -1,
                  ml: 1,
                  color: 'secondary.dark',
                  borderColor: isSelected ? 'primary.light' : 'secondary.light',
                  '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' }
                }}
              >
                <ActionIcon style={{ fontSize: '0.625rem' }} />
              </IconButton>
            );
          })}
      </Box>
    </>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func])
};
